import { auth as firebaseAuth } from "../firebase";
import BunnyUploader from "./BunnyUploader";

const normalizeApiBase = () => {
  const raw = (process.env.REACT_APP_API_URL || "https://api.spicifyapp.com").trim();
  return raw.replace(/\/+$/, "");
};

const tryParseResponse = async (response) => {
  const contentType = response.headers.get("Content-Type") || "";
  const text = (await response.text()).trim();

  if (contentType.includes("application/json")) {
    try {
      return JSON.parse(text || "{}");
    } catch (_) {
      // fall through to recovery parse
    }
  }

  const jsonStart = text.indexOf("{");
  const jsonEnd = text.lastIndexOf("}");
  if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd >= jsonStart) {
    try {
      return JSON.parse(text.substring(jsonStart, jsonEnd + 1));
    } catch (_) {}
  }

  return { error: text || "Non-JSON server response" };
};

const postWithAuth = async (url, idToken, payload) => {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(payload),
  });
  const result = await tryParseResponse(response);
  return { response, result };
};

const NotificationService = {
  /**
   * Sends a notification via the backend API
   * @param {Object} notificationData { title, message, scheduledAt, deepLink }
   * @param {File} imageFile - Optional image file to upload
   */
  async sendNotification(notificationData, imageFile) {
    try {
      const idToken = await firebaseAuth.currentUser?.getIdToken();
      if (!idToken) {
        throw new Error("Authentication required");
      }

      let imageUrl = "";
      let uploadWarning = "";
      if (imageFile) {
        // Optional image: never block notification creation if upload fails.
        try {
          imageUrl = await BunnyUploader.upload(imageFile);
        } catch (e) {
          uploadWarning = e?.message || "Image upload failed";
          console.warn("Image upload failed; sending notification without image:", e);
        }
      }

      const apiBase = normalizeApiBase();
      const endpointCandidates = apiBase.endsWith("/api")
        ? [`${apiBase}/notifications/send`, `${apiBase.replace(/\/api$/, "")}/api/notifications/send`]
        : [`${apiBase}/api/notifications/send`, `${apiBase}/notifications/send`];

      const payload = {
        title: notificationData.title,
        message: notificationData.message,
        body: notificationData.message, // compatibility with app/admin readers expecting "body"
        imageUrl: imageUrl || "",
        deepLink: notificationData.deepLink || "",
        scheduledAt: notificationData.scheduledAt || null,
        status: notificationData.status || "draft",
        target: notificationData.target || "all",
      };

      let lastError = null;
      let response;
      let result;
      for (const url of endpointCandidates) {
        try {
          const out = await postWithAuth(url, idToken, payload);
          response = out.response;
          result = out.result;
          if (response.ok) break;
          lastError = new Error(result?.error || `HTTP ${response.status}`);
        } catch (err) {
          lastError = err;
        }
      }

      if (!response || !result) {
        throw lastError || new Error("Unable to reach notifications API");
      }

      if (!response.ok) {
        return {
          success: false,
          error: result.error || "Failed to send notification",
          code: result.code || "API_ERROR",
          details: result.details
        };
      }

      return {
        success: true,
        data: result.data,
        message: uploadWarning
          ? `${result.message || "Notification sent"} (image skipped: ${uploadWarning})`
          : result.message,
        warning: uploadWarning || null,
      };

    } catch (error) {
      console.error("NotificationService Error:", error);
      return {
        success: false,
        error: error.message || "Unable to send notification",
        code: "NETWORK_ERROR",
        details: error.message
      };
    }
  }
};

export default NotificationService;